import React, { useEffect } from 'react';
import { T, M, A } from 'TOMA';
import styles from './event-exhibitors.module.scss';
import ThumbnailExhibitor from '../../../components/ThumbnailExhibitor/ThumbnailExhibitor';
import s from '../../../components/Header/Header.module.scss';
import {
  ExhibitorRepository,
  EventExhibitorFilter,
} from '../../../services/Exhibitor/Exhibitor.repository';
import { EventRepository } from 'services/Event/Event.repository';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Pagination from '../../../components/Pagination/Pagination';
import i18n from '../../../i18n';
import BreadCrumb from '../../../components/BreadCrumb/BreadCrumb';

const ITEMS_PER_PAGE = 20;

const TOPIC_LABELS: Record<string, string> = {
  region: 'Region',
  type_produit: 'Type of product',
  type_culture: 'Type of culture',
  appellation: 'Appellation',
  universe: 'Universe',
  category: 'Category',
};

const filterLabel = (f: EventExhibitorFilter): string =>
  TOPIC_LABELS[f.topic] ??
  f.topic.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export default function Events(): JSX.Element {
  const [exhibitors, setExhibitors] = React.useState<any[]>([]);
  const [event, setEvent] = React.useState<any>(null);
  const [filters, setFilters] = React.useState<EventExhibitorFilter[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [isRefreshing, setIsRefreshing] = React.useState<boolean>(true);
  const exhibitorRepo = new ExhibitorRepository();
  const eventRepo = new EventRepository();

  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryKey = searchParams.toString();
  const query = React.useMemo(
    () => Object.fromEntries(new URLSearchParams(queryKey).entries()),
    [queryKey]
  );
  const pageIndex = query.page ? parseInt(query.page, 10) || 1 : 1;
  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
  const [searchText, setSearchText] = React.useState<string>(
    query.search ?? ''
  );

  const updateQuery = (changes: Record<string, string>): void => {
    const merged: Record<string, string> = { ...query, page: '1', ...changes };
    const next = Object.fromEntries(
      Object.entries(merged).filter(([, v]) => !!v)
    );
    setSearchParams(next);
    setIsRefreshing(true);
  };

  useEffect(() => {
    eventRepo.getPublicEventBySlug(slug ?? '').then((data) => {
      const eventData = Array.isArray(data) ? data[0] : data;
      setEvent({
        ...eventData,
        logo:
          eventData.company_logo_uuid && process.env.REACT_APP_API_URL
            ? `${process.env.REACT_APP_API_URL}/storage/uploads/640/${eventData.company_logo_uuid}.${eventData.company_logo_extension}`
            : null,
      });
    });

    exhibitorRepo
      .getFiltersBySlug(slug ?? '', i18n.language)
      .then(setFilters)
      .catch(() => setFilters([]));
  }, [slug]);

  useEffect(() => {
    setIsRefreshing(true);
    exhibitorRepo
      .searchBySlug(slug ?? '', { ...query, per_page: ITEMS_PER_PAGE })
      .then((data) => {
        const formattedData = (data.data ?? []).map((exhibitor) => ({
          ...exhibitor,
          logo:
            exhibitor.logo_uuid &&
            exhibitor.logo_extension &&
            process.env.REACT_APP_API_URL
              ? `${process.env.REACT_APP_API_URL}/storage/uploads/640/${exhibitor.logo_uuid}.${exhibitor.logo_extension}`
              : null,
          showroom_logo:
            exhibitor.showroom_logo_uuid &&
            exhibitor.showroom_logo_extension &&
            process.env.REACT_APP_API_URL
              ? `${process.env.REACT_APP_API_URL}/storage/uploads/640/${exhibitor.showroom_logo_uuid}.${exhibitor.showroom_logo_extension}`
              : null,
        }));
        setExhibitors(formattedData);
        setTotal(data.total ?? 0);
      })
      .catch(() => {
        setExhibitors([]);
        setTotal(0);
      })
      .finally(() => setIsRefreshing(false));
  }, [slug, queryKey]);

  const BASE_PATH = window.location.pathname.split('/')[1];

  return (
    <>
      {event && (
        <div style={{ backgroundColor: event.color || '#000000' }}>
          <T.Block>
            <T.Container>
              <T.Flex halign="space-between" py="40" gap={4}>
                <T.Col>
                  <>
                    <T.Block my="10">
                      <A.Image
                        className="hidden"
                        src={event.logo || ''}
                        height="40"
                        width="auto"
                      />
                    </T.Block>
                    <A.Text color="white" mb="10" fontSize="24" fw="bold">
                      {event.name}
                    </A.Text>
                    <A.Text color="white" mb="10" fontSize="18">
                      {event.subtitle}
                    </A.Text>
                    <div className={s.Header__line}></div>

                    <T.Block mt="15">
                      <A.Text color="white" fontSize="18">
                        <T.Flex className="flex" gap={1}>
                          <div>
                            <A.Icon icon="calendar2-week-fill" color="white" />
                          </div>
                          <div>
                            Du {event?.date} au {event?.date_of_the_end}
                          </div>
                          <div>
                            <A.Icon icon="geo-fill" color="white" />
                          </div>
                          <div>
                            {event.city}, {event.country}
                          </div>
                        </T.Flex>
                      </A.Text>
                    </T.Block>
                  </>
                </T.Col>
              </T.Flex>
            </T.Container>
          </T.Block>
        </div>
      )}
      <div className={styles.ExhibitorList}>
        <BreadCrumb
          breadCrumb={[
            // {
            //   icon: null,
            //   href: '/' + BASE_PATH + '/preview/events',
            //   label: 'Events',
            //   isCurrent: false,
            // },
            {
              icon: null,
              href: undefined,
              label: event ? event.name : 'Event',
              isCurrent: true,
            },
            {
              icon: null,
              href: undefined,
              label: 'List of Exhibitors',
              isCurrent: false,
            },
            {
              icon: null,
              href: `/${BASE_PATH}/preview/events/${slug ?? ''}/products`,
              label: 'List of Products',
              isCurrent: false,
            },
          ]}
        />
        <h1 className={styles.ExhibitorList__title}>List of Exhibitors</h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateQuery({ search: searchText.trim() });
          }}
        >
          <div className={styles.ExhibitorList__search}>
            <A.InputText
              name="search"
              type="text"
              value={searchText}
              placeholder="Find an exhibitor, a company..."
              icon="search"
              onChange={(v) => setSearchText(v)}
            />
          </div>
        </form>

        {filters.length > 0 && (
          <div className={styles.ExhibitorList__flex}>
            {filters.map((f) => (
              <A.InputSelect
                key={f.name}
                name={f.name}
                value={query[f.name] ?? ''}
                options={f.options}
                placeholder={filterLabel(f)}
                onChange={(e) => updateQuery({ [f.name]: e.target.value })}
              />
            ))}
          </div>
        )}
        <M.Loading loading={isRefreshing} overlay>
          {exhibitors.length === 0 && (
            <div className={styles.ExhibitorList__nofound}>
              No exhibitor found...
            </div>
          )}

          <ul className={styles.ExhibitorList__List}>
            {exhibitors.map(
              ({
                id,
                logo,
                nom_societe,
                hall,
                stand,
                showroom,
                showroom_logo,
              }) => (
                <li className={styles.ExhibitorList__List__li} key={id}>
                  <ThumbnailExhibitor
                    id={id}
                    logo={logo}
                    nom_societe={nom_societe}
                    hall={hall}
                    stand={stand}
                    showroom={Number(showroom) === 1}
                    showroom_logo={showroom_logo}
                    slug={slug}
                  />
                </li>
              )
            )}
          </ul>

          <Pagination current={pageIndex} total={totalPages}>
            {(index, label, current) =>
              current ? (
                <span
                  key={label}
                  className={styles.ExhibitorList__pagination__current}
                  data-inc={label === 'Next' || label === 'Back'}
                >
                  {label}
                </span>
              ) : (
                <Link
                  key={label}
                  to={{
                    search: new URLSearchParams({
                      ...query,
                      page: String(index),
                    }).toString(),
                  }}
                  onClick={() => setIsRefreshing(true)}
                  className={styles.ExhibitorList__pagination}
                  data-inc={label === 'Next' || label === 'Back'}
                >
                  {label}
                </Link>
              )
            }
          </Pagination>
        </M.Loading>
      </div>
    </>
  );
}
